import { Box } from "../../../../../../mads";
import { Text } from "../../../../../../mads/Text";
import { useI18n } from "../../../../../../shared/I18n";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import React from "react";
import { StyleSheet } from "react-native";

type GhostwriterActionBarProps = {
  /** Whether to render a horizontal divider above the disclaimer. Used in the non-hero ViewJob layout. */
  showTopDivider?: boolean;
  /**
   * Whether the user is in the "Prefill" Ghostwriter experience (phase A
   * or its phase-D clone). These surface the generic "This feature"
   * disclaimer to cover both the prefill and the underlying auto-apply
   * flow; everyone else keeps the original auto-apply-specific copy.
   */
  isPhaseA?: boolean;
  /** The apply button section (GhostwriterApplyButton, optionally wrapped with ApplySection). */
  children: React.ReactNode;
};

/**
 * Shared Ghostwriter action bar: opaque background, optional top divider,
 * disclaimer text, and a slot for the apply button content.
 *
 * The explicit background color prevents the disclaimer text from rendering
 * on a transparent surface on iOS.
 */
export const GhostwriterActionBar = ({
  showTopDivider,
  isPhaseA,
  children,
}: GhostwriterActionBarProps) => {
  const i18n = useI18n();
  return (
    <Box sx={styles.container}>
      {showTopDivider && <Box testID="ghostwriter-action-bar-divider" sx={styles.topDivider} />}
      <Box sx={styles.disclaimerContainer}>
        <Text sx={styles.disclaimerText} level={1}>
          {isPhaseA
            ? i18n.gettext("This feature uses AI in beta and may make mistakes.")
            : i18n.gettext("Auto apply uses AI in beta and may make mistakes.")}
        </Text>
      </Box>
      {children}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.background,
  },
  topDivider: {
    height: 1,
    backgroundColor: colors.borderWeak,
    width: "100%",
  },
  disclaimerContainer: {
    paddingHorizontal: spacing.s4,
    paddingTop: spacing.s2,
    paddingBottom: spacing.s2,
  },
  disclaimerText: {
    textAlign: "center",
  },
});
